import {
    getValidAccessToken,
    isServiceAuthorized as _isServiceAuthorized,
} from './googleAuth';
import { CalendarEvent } from '../contexts/AppContext';

const BASE_URL = 'https://www.googleapis.com/calendar/v3';

export interface GoogleCalendarEvent {
    id: string;
    summary: string;
    description?: string;
    location?: string;
    start: {
        dateTime?: string;
        date?: string; // All-day events
    };
    end: {
        dateTime?: string;
        date?: string;
    };
    htmlLink: string;
}

/**
 * List events from primary calendar
 */
export async function listGoogleEvents(timeMin: string, timeMax: string): Promise<GoogleCalendarEvent[]> {
    const token = await getValidAccessToken();
    if (!token) throw new Error('Not authenticated');

    const params = new URLSearchParams({
        timeMin,
        timeMax,
        singleEvents: 'true',
        orderBy: 'startTime',
    });

    const response = await fetch(`${BASE_URL}/calendars/primary/events?${params.toString()}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to fetch events');
    }

    const data = await response.json();
    return data.items || [];
}

/**
 * Create a new event
 */
export async function createGoogleEvent(event: Partial<GoogleCalendarEvent>): Promise<GoogleCalendarEvent> {
    const token = await getValidAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${BASE_URL}/calendars/primary/events`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
    });

    if (!response.ok) {
        throw new Error('Failed to create event');
    }

    return response.json();
}

/**
 * Update an existing event
 */
export async function updateGoogleEvent(eventId: string, event: Partial<GoogleCalendarEvent>): Promise<GoogleCalendarEvent> {
    const token = await getValidAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${BASE_URL}/calendars/primary/events/${eventId}`, {
        method: 'PATCH', // Use PATCH for partial updates
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
    });

    if (!response.ok) {
        throw new Error('Failed to update event');
    }

    return response.json();
}

/**
 * Delete an event
 */
export async function deleteGoogleEvent(eventId: string): Promise<void> {
    const token = await getValidAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${BASE_URL}/calendars/primary/events/${eventId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to delete event');
    }
}

/**
 * Convert Google Event to App Event
 */
export function mapGoogleEventToAppEvent(gEvent: GoogleCalendarEvent): CalendarEvent {
    // Handle both dateTime (specific time) and date (all-day)
    const start = gEvent.start.dateTime || gEvent.start.date || new Date().toISOString();
    const end = gEvent.end.dateTime || gEvent.end.date || new Date().toISOString();

    // Format time for app (HH:mm AM/PM) if it's not all day
    // This part depends on how App uses timeStart/timeEnd.
    // App uses "10:00 AM" strings currently.
    // We need to parse ISO to that format.

    // Extract date string YYYY-MM-DD
    const dateStr = start.split('T')[0];

    return {
        id: `google-${gEvent.id}`, // specific prefix to identify source? OR reuse ID
        googleEventId: gEvent.id, // We'll add this field to AppContext
        title: gEvent.summary || '(No Title)',
        date: dateStr,
        timeStart: formatTime(start),
        timeEnd: formatTime(end),
        type: 'meeting', // Default type
        color: '#4285F4', // Google Blue
        location: gEvent.location,
        isProtected: false,
    };
}

/**
 * Helper to format ISO string to "10:00 AM"
 */
function formatTime(isoString: string): string {
    if (!isoString.includes('T')) return 'All Day'; // Simple check for date-only

    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
}

/**
 * Convert App Event to Google Event Resource
 */
export function mapAppEventToGoogleEvent(appEvent: CalendarEvent, date: Date): Partial<GoogleCalendarEvent> {
    // We need the date context because appEvent.timeStart is just "10:00 AM"
    // So we combine date + time string to get ISO

    // Function to parse "10:00 AM" to ISO string
    const getIso = (timeStr: string) => {
        const [time, period] = timeStr.split(' ');
        let [hours, minutes] = time.split(':').map(Number);

        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;

        const d = new Date(date);
        d.setHours(hours, minutes, 0, 0);
        return d.toISOString();
    };

    return {
        summary: appEvent.title,
        location: appEvent.location,
        start: {
            dateTime: getIso(appEvent.timeStart),
        },
        end: {
            dateTime: getIso(appEvent.timeEnd),
        }
    };
}
